import { Link } from 'react-router-dom';
import { Carousel } from './Carousel';
import { C, F } from './SiteChrome';
import { imageSrc } from '../lib/blogApi';

/**
 * Long date, deliberately NOT the app's dd/mm/yy (lib/date.js). The blog is a
 * public marketing page where a spelled-out date reads better and carries more
 * weight than 03/09/26.
 */
export const formatPostDate = (iso) =>
    new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', {
        day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
    });

/**
 * Featured image cropped to a fixed height (the small square in the sidebar),
 * or the brand gradient while there isn't one yet, so a card is the same shape
 * with or without artwork. Images are uploaded in the blog editor and served by
 * the backend, hence imageSrc. Which part shows is set in the editor.
 */
export const PostImage = ({ post, height }) => (
    post.image
        ? <img
            src={imageSrc(post.image)}
            alt={post.imageAlt}
            style={{ display: 'block', width: '100%', height, objectFit: 'cover', objectPosition: post.imagePosition || 'center' }}
        />
        : <div aria-hidden="true" style={{ height, background: `linear-gradient(140deg, ${C.primary} 0%, ${C.deep} 100%)` }} />
);

// The text sits on a partly see-through panel over the bottom of the picture,
// which fills the whole card behind it.
// Fixed line counts (with a minimum height to match), so the panel is the same
// size on every card whatever the length of its title and summary.
const CLAMP = (lines) => ({
    display: '-webkit-box',
    WebkitLineClamp: lines,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
});

const PANEL = {
    background: 'rgba(255, 255, 255, 0.76)',
    backdropFilter: 'blur(3px)',
    WebkitBackdropFilter: 'blur(3px)',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
};

/**
 * One post teaser. Used by the landing page's blog section, the blog page and,
 * in its `compact` form, by the sidebar on a post page, so they can't drift
 * apart. Compact keeps the image as a small square beside the title and drops
 * the excerpt, which will not fit a 300px column. The big card has the
 * picture filling it, with the text on a partly see-through panel.
 */
export const BlogCard = ({ post, compact = false }) => (compact ? (
    <Link
        to={`/${post.slug}`}
        className="mq-blogcard mq-arrowbtn mq-lift"
        style={{
            display: 'flex',
            gap: 14,
            background: '#ffffff',
            border: `1px solid ${C.borderLight}`,
            borderRadius: 20,
            padding: 14,
            textDecoration: 'none',
        }}
    >
        <div style={{ width: 76, height: 76, borderRadius: 14, overflow: 'hidden', flexShrink: 0 }}>
            <PostImage post={post} height={76} />
        </div>
        <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 11.5, fontWeight: 600, color: C.light, marginBottom: 5 }}>
                {formatPostDate(post.date)} · {post.readTime}
            </div>
            <div style={{ fontFamily: F.brand, fontSize: 14.5, fontWeight: 700, lineHeight: 1.35, color: C.deep }}>
                {post.title}
            </div>
            <div style={{ marginTop: 8, fontSize: 13, fontWeight: 700, color: C.primary }}>
                Read post<span className="mq-arrow" aria-hidden="true">&rarr;</span>
            </div>
        </div>
    </Link>
) : (
    <Link
        to={`/${post.slug}`}
        className="mq-blogcard mq-arrowbtn mq-lift"
        style={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            background: `linear-gradient(140deg, ${C.primary} 0%, ${C.deep} 100%)`,
            border: `1px solid ${C.borderLight}`,
            borderRadius: 24,
            overflow: 'hidden',
            textDecoration: 'none',
            minHeight: 400,
            // Fills the carousel slide, so every card in a row is the same
            // height whatever the length of its excerpt.
            height: '100%',
        }}
    >
        {post.image && (
            <img
                src={imageSrc(post.image)}
                alt={post.imageAlt || ''}
                style={{
                    position: 'absolute', inset: 0, width: '100%', height: '100%',
                    objectFit: 'cover', objectPosition: post.imagePosition || 'center',
                }}
            />
        )}
        <div style={{ ...PANEL, padding: '18px 22px 22px', gap: 8 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: C.light }}>
                {formatPostDate(post.date)} · {post.readTime}
            </div>
            <div style={{ fontFamily: F.brand, fontSize: 17.5, fontWeight: 700, lineHeight: 1.35, color: C.deep, ...CLAMP(2), minHeight: '2.7em' }}>
                {post.title}
            </div>
            <div style={{ fontSize: 14, lineHeight: 1.6, color: C.bodyOnWhite, ...CLAMP(3), minHeight: '4.8em' }}>{post.excerpt}</div>
            {/* mq-arrowbtn sits on the card itself, so the arrow slides out
                when the whole card is hovered, not just this line. */}
            <div style={{ paddingTop: 6, fontSize: 14, fontWeight: 700, color: C.primary }}>
                Read post<span className="mq-arrow" aria-hidden="true">&rarr;</span>
            </div>
        </div>
    </Link>
));

/**
 * Every published post in one sideways row: swipe on a phone, dots on a
 * desktop, and the same slow auto-advance as the testimonials, which is the
 * same Carousel underneath.
 */
export const BlogCarousel = ({ posts }) => (
    <Carousel label="Posts page" intervalMs={0} autoscroll={25}>
        {posts.map((post) => <BlogCard key={post.slug} post={post} />)}
    </Carousel>
);
